import { useState, useEffect } from 'react';
import {
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  Users,
  Search,
  Upload,
  Check,
  Loader2,
  Mail,
  Ban
} from 'lucide-react';

import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { getAuthHeaders, adminFetch } from '../../../services/adminApi';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function NewsletterRecipientsTab({ showMessage }) {
  const [recipients, setRecipients] = useState([]);
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [formData, setFormData] = useState({ email: '', name: '', subscribed_list_ids: [] });
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [filterList, setFilterList] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [importData, setImportData] = useState('');
  const [importListId, setImportListId] = useState('');
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    fetchLists();
    fetchRecipients();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchRecipients();
    }, 300);
    return () => clearTimeout(timer);
  }, [search, filterList]);

  const fetchLists = async () => {
    try {
      const response = await adminFetch(`${API_BASE}/api/admin/newsletter/lists`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setLists(data);
      }
    } catch (error) {
      console.error('Error fetching lists:', error);
    }
  };

  const fetchRecipients = async () => {
    setLoading(true);
    try {
      let url = `${API_BASE}/api/admin/newsletter/recipients?include_unsubscribed=true&limit=100`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (filterList) url += `&list_id=${filterList}`;

      const response = await adminFetch(url, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setRecipients(data);
      }

      // Get total count
      const countUrl = `${API_BASE}/api/admin/newsletter/recipients/count?include_unsubscribed=true`;
      const countResponse = await adminFetch(countUrl, {
        headers: getAuthHeaders()
      });
      if (countResponse.ok) {
        const countData = await countResponse.json();
        setTotalCount(countData.count);
      }
    } catch (error) {
      console.error('Error fetching recipients:', error);
      showMessage('Failed to load recipients', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (recipient) => {
    setEditing(recipient);
    setFormData({
      email: recipient.email,
      name: recipient.name || '',
      subscribed_list_ids: recipient.subscribed_list_ids || []
    });
    setShowForm(true);
  };

  const handleCreate = () => {
    setEditing(null);
    setFormData({ email: '', name: '', subscribed_list_ids: [] });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formData.email.trim()) {
      showMessage('Email is required', 'error');
      return;
    }

    setSaving(true);
    try {
      const url = editing
        ? `${API_BASE}/api/admin/newsletter/recipients/${editing.id}`
        : `${API_BASE}/api/admin/newsletter/recipients`;
      const method = editing ? 'PUT' : 'POST';

      const response = await adminFetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        showMessage(editing ? 'Recipient updated' : 'Recipient added');
        setShowForm(false);
        setEditing(null);
        fetchRecipients();
        fetchLists(); // Refresh list counts
      } else {
        const error = await response.json();
        showMessage(error.detail || 'Failed to save recipient', 'error');
      }
    } catch (error) {
      console.error('Error saving recipient:', error);
      showMessage('Failed to save recipient', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (recipient) => {
    if (!window.confirm(`Are you sure you want to delete ${recipient.email}?`)) {
      return;
    }

    try {
      const response = await adminFetch(`${API_BASE}/api/admin/newsletter/recipients/${recipient.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        showMessage('Recipient deleted');
        fetchRecipients();
        fetchLists(); // Refresh list counts
      } else {
        showMessage('Failed to delete recipient', 'error');
      }
    } catch (error) {
      console.error('Error deleting recipient:', error);
      showMessage('Failed to delete recipient', 'error');
    }
  };

  const toggleListSubscription = (listId) => {
    const current = formData.subscribed_list_ids;
    if (current.includes(listId)) {
      setFormData({
        ...formData,
        subscribed_list_ids: current.filter(id => id !== listId)
      });
    } else {
      setFormData({
        ...formData,
        subscribed_list_ids: [...current, listId]
      });
    }
  };

  const handleImport = async () => {
    if (!importData.trim()) {
      showMessage('Please enter email addresses to import', 'error');
      return;
    }

    setImporting(true);
    try {
      // Parse import data (one email per line, or comma separated)
      const lines = importData.split(/[\n,]/).map(l => l.trim()).filter(Boolean);
      const recipients = lines.map(line => {
        // Check if format is "Name <email>" or just "email"
        const match = line.match(/^(.+?)\s*<(.+@.+)>$/) || line.match(/^(.+@.+)$/);
        if (match) {
          if (match[2]) {
            return { email: match[2].trim(), name: match[1].trim(), subscribed_list_ids: [] };
          }
          return { email: match[1].trim(), name: null, subscribed_list_ids: [] };
        }
        return null;
      }).filter(Boolean);

      if (recipients.length === 0) {
        showMessage('No valid email addresses found', 'error');
        setImporting(false);
        return;
      }

      const response = await adminFetch(`${API_BASE}/api/admin/newsletter/recipients/import`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          recipients,
          list_id: importListId || null
        })
      });

      if (response.ok) {
        const result = await response.json();
        showMessage(`Imported ${result.imported} recipients (${result.skipped} skipped)`);
        setShowImport(false);
        setImportData('');
        setImportListId('');
        fetchRecipients();
        fetchLists();
      } else {
        const error = await response.json();
        showMessage(error.detail || 'Failed to import recipients', 'error');
      }
    } catch (error) {
      console.error('Error importing recipients:', error);
      showMessage('Failed to import recipients', 'error');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header with Search and Actions */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="flex flex-1 gap-2">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by email or name..."
              className="pl-10"
            />
          </div>
          <select
            value={filterList}
            onChange={(e) => setFilterList(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-background text-foreground"
          >
            <option value="">All Lists</option>
            {lists.map(list => (
              <option key={list.id} value={list.id}>{list.name}</option>
            ))}
          </select>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowImport(true)}>
            <Upload className="h-4 w-4 mr-2" />
            Import
          </Button>
          <Button onClick={handleCreate} variant="adminPrimary">
            <Plus className="h-4 w-4 mr-2" />
            Add Recipient
          </Button>
        </div>
      </div>

      {/* Stats */}
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Showing {recipients.length} of {totalCount} recipient{totalCount !== 1 ? 's' : ''}
      </p>

      {/* Recipients List */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : recipients.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p>{search || filterList ? 'No recipients match your filters' : 'No recipients yet'}</p>
          <p className="text-sm">Add recipients to start building your newsletter audience</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                  Lists
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-background divide-y divide-gray-200">
              {recipients.map((recipient) => (
                <tr key={recipient.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-gray-400" />
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{recipient.email}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 hidden sm:table-cell">
                    {recipient.name || '-'}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <div className="flex flex-wrap gap-1">
                      {recipient.subscribed_list_ids?.length > 0 ? (
                        recipient.subscribed_list_ids.slice(0, 3).map(listId => {
                          const list = lists.find(l => l.id === listId);
                          return list ? (
                            <span key={listId} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                              {list.name}
                            </span>
                          ) : null;
                        })
                      ) : (
                        <span className="text-xs text-gray-400">No lists</span>
                      )}
                      {recipient.subscribed_list_ids?.length > 3 && (
                        <span className="text-xs text-gray-400">+{recipient.subscribed_list_ids.length - 3} more</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {recipient.global_unsubscribed ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                        <Ban className="h-3 w-3" />
                        Unsubscribed
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                        <Check className="h-3 w-3" />
                        Active
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(recipient)}
                        className="h-8 w-8 p-0"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(recipient)}
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Recipient' : 'Add Recipient'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="email@example.com"
                disabled={!!editing}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="John Doe"
              />
            </div>
            <div className="space-y-2">
              <Label>Subscribe to Lists</Label>
              <div className="space-y-2 max-h-[200px] overflow-y-auto border rounded-md p-2">
                {lists.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-2">No lists available</p>
                ) : (
                  lists.map(list => (
                    <label key={list.id} className="flex items-center gap-2 p-2 hover:bg-gray-50 dark:hover:bg-gray-800 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.subscribed_list_ids.includes(list.id)}
                        onChange={() => toggleListSubscription(list.id)}
                        className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                      <span className="text-sm">{list.name}</span>
                    </label>
                  ))
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowForm(false)}>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                variant="adminPrimary"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                {editing ? 'Update' : 'Add'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={showImport} onOpenChange={setShowImport}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Import Recipients</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Email Addresses</Label>
              <textarea
                value={importData}
                onChange={(e) => setImportData(e.target.value)}
                placeholder="Enter email addresses (one per line or comma-separated)&#10;&#10;Examples:&#10;john@example.com&#10;Jane Doe <jane@example.com>"
                rows={8}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-background text-foreground resize-none"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Supports: email@example.com or Name &lt;email@example.com&gt;
              </p>
            </div>
            <div className="space-y-2">
              <Label>Auto-subscribe to List (optional)</Label>
              <select
                value={importListId}
                onChange={(e) => setImportListId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-background text-foreground"
              >
                <option value="">Don't auto-subscribe</option>
                {lists.map(list => (
                  <option key={list.id} value={list.id}>{list.name}</option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowImport(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleImport}
                disabled={importing}
                variant="adminPrimary"
              >
                {importing ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4 mr-2" />
                )}
                Import
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
